import { SearchSmall } from '@zoopla-eng/zds-components/icons';
import { fontGroups } from '@zoopla-eng/zds-components/themes';
import { SearchContext } from '@zoopla/listing-search-context';
import {
    getDuration,
    getFormValuesFromQuery,
    getQ,
    getTransportType
} from '@zoopla/listing-search/filters/modal';
import {
    buildSelectBedsTitle,
    buildSelectPriceTitleForSale,
    buildSelectPriceTitleToRent
} from '@zoopla/redux-reducer-filters';
import { SearchPageType } from '@zoopla/redux-types';
import { NextRouter, useRouter } from 'next/router';
import { FC, useContext, useEffect, useMemo, useState } from 'react';
import {
    dividerStyle,
    mobileQuickSearchBarWrapperStyle,
    styledLocationTextStyle,
    styledRadiusTextStyle,
    textStyle,
    wrapperStyle
} from './MobileQuickSearchButton.styles.css';
import { trackQuickSearchButtonClick } from './MobileQuickSearchButton.tracking';

type ModalTypes = 'search_initiation' | null;

const translationsMap = new Map([
    ['studio', 'studio'],
    ['studios', 'studios'],
    ['bed', 'bed'],
    ['beds', 'beds'],
    ['min', 'min'],
    ['max', 'max'],
    ['default', 'Any beds']
]);

const radiiOptions = [
    { value: '0', text: 'This area only' },
    { value: '0.25', text: '+ ¼ mile' },
    { value: '0.5', text: '+ ½ mile' },
    { value: '1', text: '+ 1 miles' },
    { value: '3', text: '+ 3 miles' },
    { value: '5', text: '+ 5 miles' },
    { value: '10', text: '+ 10 miles' },
    { value: '15', text: '+ 15 miles' },
    { value: '20', text: '+ 20 miles' },
    { value: '30', text: '+ 30 miles' },
    { value: '40', text: '+ 40 miles' }
];

const propertySubTypeOptions: Record<string, string> = {
    detached: 'Detached',
    semi_detached: 'Semi-detached',
    terraced: 'Terraced',
    flats: 'Flats',
    bungalow: 'Bungalows',
    farms_land: 'Farms/land',
    park_home: 'Park homes'
};

const durationOptionsText: Record<string, string> = {
    '900': '15 minutes',
    '1800': '30 minutes',
    '2700': '45 minutes',
    '3600': '1 hour',
    '4500': '1 ¼ hour'
};

const transportTypeOptionsText: Record<string, string> = {
    driving: 'Driving',
    walking: 'Walking',
    walking_train: 'Train',
    public_transport: 'Public transport',
    cycling: 'Cycling'
};

function coerceString(val: string | string[]) {
    return Array.isArray(val) ? val[0] : val;
}

const getLocation = (query: NextRouter['query']) => {
    const q = getQ(query);
    const coercedQ = Array.isArray(q) ? q[0] : q;
    if (coercedQ !== '') return coercedQ;
    return;
};

function buildDisplayText(
    query: NextRouter['query'],
    searchType: string,
    pathname: string
) {
    const isForSaleSearch =
        searchType === 'for-sale' || searchType === 'new-homes';
    const formValue = getFormValuesFromQuery({ pathname, query });

    const radiusLabel = (
        radiiOptions.find((option) => {
            if (formValue.radius !== '') {
                return option.value === formValue.radius;
            }
            return false;
        }) ?? radiiOptions[0]
    )?.text;

    const propertyTypeLabel =
        formValue.property_sub_type
            .map((propertySubType) => propertySubTypeOptions[propertySubType])
            .join(', ') || 'Show all';

    const selectBedsTitle = buildSelectBedsTitle(
        parseInt(coerceString(formValue.beds_min), 10),
        parseInt(coerceString(formValue.beds_max), 10),
        translationsMap
    );

    const selectPriceTitle = isForSaleSearch
        ? buildSelectPriceTitleForSale(
              coerceString(formValue.price_min),
              coerceString(formValue.price_max)
          )
        : buildSelectPriceTitleToRent(
              coerceString(formValue.price_min),
              coerceString(formValue.price_max),
              coerceString(formValue.price_min),
              coerceString(formValue.price_max),
              coerceString(formValue.price_frequency) ?? 'per_month'
          );

    const isTravelTimeSearch = Boolean(
        getDuration(query) && getTransportType(query)
    );

    const durationTitleDisplay =
        durationOptionsText[coerceString(formValue.duration)];
    const travelTypeDurationType =
        transportTypeOptionsText[coerceString(formValue.transport_type)];
    const title = `${durationTitleDisplay} • ${travelTypeDurationType}`;

    return {
        radius: radiusLabel,
        propertyType: propertyTypeLabel,
        beds: selectBedsTitle,
        price: selectPriceTitle,
        timeTravel: isTravelTimeSearch ? title : null
    };
}

export type Props = {
    openModal: (type: ModalTypes) => void;
    searchType: SearchPageType;
};

export const MobileQuickSearchButton: FC<React.PropsWithChildren<Props>> = ({
    openModal,
    searchType = 'for-sale'
}) => {
    const { pathname, query, isReady } = useRouter();
    const { state } = useContext(SearchContext);
    const [locationText, setLocationText] = useState(
        getLocation(query) ?? state.locationByIdentifier ?? ''
    );

    useEffect(() => {
        if (isReady && !locationText) {
            setLocationText(getLocation(query) ?? '');
        }
    }, [isReady, query, locationText]);

    useEffect(() => {
        if (
            state.locationByIdentifier !== null &&
            state.locationByIdentifier !== ''
        ) {
            setLocationText(state.locationByIdentifier);
        } else {
            setLocationText(getLocation(query) ?? '');
        }
    }, [state.locationByIdentifier, query]);

    const labels = useMemo(
        () => buildDisplayText(query, searchType, pathname),
        [query, searchType, pathname]
    );

    return (
        <div
            className={wrapperStyle({ disabled: !isReady })}
            onClick={() => {
                if (isReady) {
                    openModal('search_initiation');
                    trackQuickSearchButtonClick();
                }
            }}
            data-testid="testid_quick_search_modal_button"
            aria-label="Quick search modal"
        >
            <div className={mobileQuickSearchBarWrapperStyle}>
                <div>
                    <SearchSmall accessibilityType="decorative" />
                </div>
                <p
                    className={`${textStyle({ disabled: !isReady })} ${
                        fontGroups.ladle
                    }`}
                >
                    <span
                        className={styledLocationTextStyle}
                        color="text-title"
                    >
                        {locationText}
                    </span>
                    <span className={styledRadiusTextStyle}>
                        <span className={dividerStyle} />
                        {`${labels.timeTravel ?? labels.radius}`}
                    </span>
                    <span className={dividerStyle} />
                    {labels.beds}
                    <span className={dividerStyle} />
                    {labels.price}
                    <span className={dividerStyle} />
                    {labels.propertyType}
                </p>
            </div>
        </div>
    );
};
